import { locateFaceMeshAsset } from '../../../shared/mediapipeAssets';

let faceMeshPromise = null;
const landmarkCache = new Map();

function cacheKey(src, width, height) {
  return `${src || ''}::${width}x${height}`;
}

async function getFaceMesh() {
  if (!faceMeshPromise) {
    faceMeshPromise = (async () => {
      const { FaceMesh } = await import('@mediapipe/face_mesh');
      const mesh = new FaceMesh({ locateFile: locateFaceMeshAsset });
      mesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      return mesh;
    })().catch((err) => {
      faceMeshPromise = null;
      throw err;
    });
  }
  return faceMeshPromise;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image source'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for landmarks'));
    img.src = src;
  });
}

/**
 * Detect MediaPipe Face Mesh landmarks on a frontal image (normalized 0–1).
 * Results are cached per image URL + natural size.
 */
export async function detectFrontalFaceLandmarks(imageUrl) {
  if (!imageUrl) return null;

  const img = await loadImageElement(imageUrl);
  const W = img.naturalWidth || img.width;
  const H = img.naturalHeight || img.height;
  if (!W || !H) return null;

  const key = cacheKey(imageUrl, W, H);
  if (landmarkCache.has(key)) {
    return landmarkCache.get(key);
  }

  const mesh = await getFaceMesh();

  const landmarks = await new Promise((resolve, reject) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      reject(err);
    };

    mesh.onResults((results) => {
      const lm = results?.multiFaceLandmarks?.[0] || null;
      finish(lm);
    });

    mesh.send({ image: img }).catch(fail);
    setTimeout(() => fail(new Error('Face landmark timeout')), 12000);
  });

  if (landmarks?.length) {
    landmarkCache.set(key, { landmarks, width: W, height: H });
  }

  return landmarks?.length ? { landmarks, width: W, height: H } : null;
}

export function clearFrontalFaceLandmarkCache() {
  landmarkCache.clear();
}
